/**
 * Customer detail + edit. Reached by tapping a customer row on the Customers list. Fetches the
 * customer (real GET /api/customers/:id round-trip) and renders a view + an editable form: name,
 * company, notes, plus the primary contact's name/phone/email and the billing address. Save issues
 * the real PATCH /api/customers/:id and invalidates both the detail and the customers-list queries so
 * the change is reflected everywhere.
 *
 * Owner/office gating is server-side; a 403 surfaces as the rendered error. NEVER imports @smartbox/*
 * (mobile invariant 1). Design-system primitives only; no raw styles.
 */
import { useState } from 'react';
import { ActivityIndicator, StyleSheet, View } from 'react-native';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getCustomer, updateCustomer, type CustomerDetail, type UpdateCustomerRequest } from '../../../src/api/crm';
import { Screen, Text, Card, Button, Field, Select } from '../../../src/components/ui';
import { VoiceField } from '../../../src/components/voice/VoiceField';
import { theme } from '../../../src/theme';
import { extractErrorMessage, humanize } from '../../../src/lib/format';

const TYPE_OPTIONS = [
  { value: 'residential', label: 'Residential' },
  { value: 'commercial', label: 'Commercial' },
  { value: 'government', label: 'Government' },
] as const;

type CustomerType = 'residential' | 'commercial' | 'government';

/** Mounted only once the customer is loaded, so useState seeds from the fetched detail. */
function CustomerEditForm({ customer, onSaved }: { customer: CustomerDetail; onSaved: () => void }) {
  const [displayName, setDisplayName] = useState(customer.displayName);
  const [companyName, setCompanyName] = useState(customer.companyName ?? '');
  const [type, setType] = useState<CustomerType>(
    (['residential', 'commercial', 'government'].includes(customer.customerType) ? customer.customerType : 'residential') as CustomerType,
  );
  const [contactName, setContactName] = useState(customer.primaryContact?.fullName ?? '');
  const [phone, setPhone] = useState(customer.primaryContact?.phone ?? '');
  const [email, setEmail] = useState(customer.primaryContact?.email ?? '');
  const [line1, setLine1] = useState(customer.billingAddress?.line1 ?? '');
  const [city, setCity] = useState(customer.billingAddress?.city ?? '');
  const [stateField, setStateField] = useState(customer.billingAddress?.state ?? '');
  const [postal, setPostal] = useState(customer.billingAddress?.postalCode ?? '');
  const [error, setError] = useState<string | null>(null);

  const mut = useMutation({
    mutationFn: (body: UpdateCustomerRequest) => updateCustomer(customer.id, body),
    onSuccess: () => { setError(null); onSaved(); },
    onError: (err) => setError(extractErrorMessage(err, 'Could not save the customer.')),
  });

  const onSave = () => {
    if (!displayName.trim()) return setError('Enter a customer name.');
    const body: UpdateCustomerRequest = {
      display_name: displayName.trim(),
      company_name: companyName.trim() || null,
      customer_type: type,
      contact_name: contactName.trim() || undefined,
      // Send null (not undefined) for cleared phone/email so an intentional clear persists.
      phone: phone.trim() || null,
      email: email.trim() || null,
    };
    // Only send address when at least one field is filled — never create/patch an empty address row.
    if (line1.trim() || city.trim() || stateField.trim() || postal.trim()) {
      body.address = { line1: line1.trim(), city: city.trim(), state: stateField.trim(), postal_code: postal.trim() };
    }
    mut.mutate(body);
  };

  return (
    <Card>
      <Text variant="bodyStrong">Edit customer</Text>
      <Select label="Type" options={TYPE_OPTIONS} value={type} onChange={(v) => setType(v)} />
      <VoiceField label="Name" placeholder="e.g. Jane Homeowner" value={displayName} onChangeText={setDisplayName} accessibilityLabel="Name" micAccessibilityLabel="Dictate name" />
      <VoiceField label="Company" placeholder="e.g. Acme LLC" value={companyName} onChangeText={setCompanyName} accessibilityLabel="Company" micAccessibilityLabel="Dictate company" />
      <Text variant="label" color="secondary" style={styles.fieldLabel}>Primary contact</Text>
      <VoiceField label="Contact name" placeholder="Full name" value={contactName} onChangeText={setContactName} accessibilityLabel="Contact name" micAccessibilityLabel="Dictate contact name" />
      <Field label="Phone" placeholder="+1 555 555 5555" value={phone} onChangeText={setPhone} keyboardType="phone-pad" accessibilityLabel="Phone" />
      <Field label="Email" placeholder="name@example.com" value={email} onChangeText={setEmail} keyboardType="email-address" autoCapitalize="none" accessibilityLabel="Email" />
      <Text variant="label" color="secondary" style={styles.fieldLabel}>Billing address</Text>
      <VoiceField label="Street" placeholder="123 Main St" value={line1} onChangeText={setLine1} accessibilityLabel="Street" micAccessibilityLabel="Dictate street" />
      <VoiceField label="City" placeholder="City" value={city} onChangeText={setCity} accessibilityLabel="City" micAccessibilityLabel="Dictate city" />
      <VoiceField label="State" placeholder="State" value={stateField} onChangeText={setStateField} accessibilityLabel="State" micAccessibilityLabel="Dictate state" />
      <Field label="Postal code" placeholder="00000" value={postal} onChangeText={setPostal} accessibilityLabel="Postal code" />
      {error ? (
        <Text variant="bodyStrong" color="danger" accessibilityRole="alert">{error}</Text>
      ) : null}
      <Button title="Save changes" onPress={onSave} loading={mut.isPending} disabled={mut.isPending} accessibilityLabel="Save changes" />
    </Card>
  );
}

export function CustomerDetailScreen() {
  const { id } = useLocalSearchParams<{ id: string }>();
  const router = useRouter();
  const queryClient = useQueryClient();

  const customerQuery = useQuery({
    queryKey: ['customer', id],
    queryFn: () => getCustomer(id!),
    enabled: !!id,
    staleTime: 30_000,
  });

  if (customerQuery.isLoading) {
    return (
      <Screen>
        <View style={styles.centered}>
          <ActivityIndicator size="large" color={theme.color.action.primary} accessibilityLabel="Loading customer" />
        </View>
      </Screen>
    );
  }

  if (customerQuery.isError || !customerQuery.data) {
    return (
      <Screen>
        <Card>
          <Text variant="bodyStrong" color="danger" accessibilityRole="alert">
            {extractErrorMessage(customerQuery.error, 'Failed to load the customer.')}
          </Text>
          <Button title="Retry" variant="secondary" size="sm" fullWidth={false} onPress={() => customerQuery.refetch()} />
        </Card>
      </Screen>
    );
  }

  const customer = customerQuery.data;

  return (
    <Screen scroll>
      <Text variant="title">{customer.displayName}</Text>
      <Card>
        <Text variant="small" color="secondary">{humanize(customer.customerType)}</Text>
        {customer.companyName ? <Text variant="small" color="secondary">{customer.companyName}</Text> : null}
        <Text variant="small" color="secondary">
          {customer.estimateCount} {customer.estimateCount === 1 ? 'estimate' : 'estimates'} · {customer.jobCount} {customer.jobCount === 1 ? 'job' : 'jobs'}
        </Text>
        {customer.primaryContact?.phone ? <Text variant="small" color="secondary">{customer.primaryContact.phone}</Text> : null}
      </Card>

      <CustomerEditForm
        customer={customer}
        onSaved={() => {
          void queryClient.invalidateQueries({ queryKey: ['customer', id] });
          void queryClient.invalidateQueries({ queryKey: ['customers'] });
          router.replace('/(app)/customers');
        }}
      />
    </Screen>
  );
}

const styles = StyleSheet.create({
  centered: { flex: 1, justifyContent: 'center', alignItems: 'center', gap: theme.spacing.md },
  fieldLabel: { marginTop: theme.spacing.sm, marginBottom: theme.spacing.xs },
});
